// ─────────────────────────────────────────────────────────────────────────────
// useReports — GET /api/rexy/report-index + POST /api/rexy/report-answer.
// Shared core (owner ruling 2026-07-31, "one core, every window").
//
// `answer()` is a GATE, not a render concern, so it lives here: any surface that
// offers LISTEN / SET ASIDE answers through the SAME contract the chat/call
// reminder uses — never a side path. Both surfaces therefore inherit the honest
// state machine: pending until the durable push persists, ✓ delivered only after.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { makeTimeFormatter } from './timeFormat';
import { REPORT_INDEX, REPORT_ANSWER } from './endpoints';

export type ReportWindowSpan = { start: string | null; end: string };

export type ReportEntry = {
  report_id: string;
  kind: 'scheduled_6am' | 'boundary' | 'asked';
  boundary: string | null;          // '06:00' | '12:00' | '18:00' | '00:00' (local label)
  composed_at: string;
  windows: ReportWindowSpan[];      // >1 ⇒ a merged mesh across carried windows
  merged: boolean;
  content: string;                  // her authored report, VERBATIM
  status: 'complete' | 'record_failed';
  // delivered: the durable chat push TRUTHFULLY persisted — delivered means
  // delivered, finalized only after the write committed.
  // pending_delivery: bound for delivery (6am/asked compose, or accepted) but
  // the push has not yet persisted — shown pending, NEVER ✓ delivered.
  // waiting: composed, reminder out, accept/reject pending.
  // carried: rejected / not listened — put to the side, will merge forward.
  // superseded: its window was re-reasoned into a later merged report.
  // failed: the Tier-2 record never landed — NEVER deliverable, no answer path
  // (the false-success inversion, closed).
  state: 'delivered' | 'pending_delivery' | 'waiting' | 'carried' | 'superseded' | 'failed';
  // Exact-mirror read outcome: content is the TIER-2 record's body ('ok');
  // 'mismatch'/'missing' are loud parity failures; 'legacy'/'failed' are honest.
  mirror: 'ok' | 'mismatch' | 'missing' | 'legacy' | 'failed';
  notes_total: number;
  superseded_by?: string | null;
};

export type ReportsGate = {
  due_boundary: string | null;      // an uncomposed boundary instant (catch-up pending)
  waiting_on_note: boolean;         // complete-after-landed-note: compose deferred
  store_status: string;
  ledger_status: string;
};

export type ReportsView = {
  store_status: 'ok' | 'fresh' | 'unreadable';
  ledger_status: 'ok' | 'fresh' | 'unreadable';
  timezone: string;
  pending: { report_id: string; composed_at: string; boundary: string | null; reminder: string } | null;
  gate: ReportsGate | null;
  windows: ReportEntry[];           // newest-first
};
export type ReportsResponse = { success: boolean; reports: ReportsView; error?: string };

export type ReportsFeed = {
  view: ReportsView | null;
  err: string | null;
  updatedAt: string | null;
  answering: string | null;
  /** report_id → the Tier-2 body returned by an accepted answer, shown immediately. */
  heardNow: Record<string, string>;
  answer: (reportId: string, verdict: 'accept' | 'reject') => void;
  refetch: () => void;
  timeLabel: (iso: string) => string;
  stampLabel: (iso: string) => string;
};

export function useReports(): ReportsFeed {
  const [view, setView] = useState<ReportsView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [answering, setAnswering] = useState<string | null>(null);
  // LISTEN shows the report IMMEDIATELY from the answer response's returned
  // body (the Tier-2 record — never just a refresh), per report_id.
  const [heardNow, setHeardNow] = useState<Record<string, string>>({});
  const timeLabel = useMemo(() => makeTimeFormatter(view?.timezone), [view?.timezone]);
  const stampLabel = useMemo(() => makeTimeFormatter(view?.timezone, true), [view?.timezone]);

  const fetchReports = useCallback(() => {
    fetch(REPORT_INDEX, { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success && j.reports) {
          setView((j as ReportsResponse).reports);
          setErr(null);
          setUpdatedAt(new Date().toISOString());
        } else setErr((j && j.error) || 'unavailable');
      })
      .catch((e) => setErr(String(e)));
  }, []);

  // fetchReports is referenced by answer() below; hold it in a ref so answer's
  // identity stays stable for consumers that memoise on it.
  const fetchRef = useRef(fetchReports);
  fetchRef.current = fetchReports;

  useEffect(() => {
    fetchReports();
    const poll = setInterval(fetchReports, POLL_MS);
    return () => clearInterval(poll);
  }, [fetchReports]);

  // The SAME contract the chat/call reminder answers through — a surface is a
  // second, honest affordance on POST /api/rexy/report-answer, never a side path.
  const answer = useCallback((reportId: string, verdict: 'accept' | 'reject') => {
    setAnswering(reportId);
    fetch(REPORT_ANSWER, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ report_id: reportId, answer: verdict }),
    })
      .then((r) => r.json())
      .then((j) => {
        // Accepted → display the RETURNED Tier-2 body immediately (LISTEN
        // shows the report now). The STATE stays honest: pending until the
        // durable push persists, ✓ delivered only after.
        if (j && j.success && j.result && j.result.content
            && (j.result.status === 'pending_delivery' || j.result.status === 'delivered')) {
          setHeardNow((prev) => ({ ...prev, [reportId]: j.result.content }));
        }
        fetchRef.current();
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setAnswering(null));
  }, []);

  return {
    view, err, updatedAt, answering, heardNow, answer,
    refetch: fetchReports, timeLabel, stampLabel,
  };
}
